import {
  buildUnifiedPaywallState,
  filterGrantingSubscriptions,
  prefetchAccessBootstrap,
  transformTeams,
  type PaywallResourceAccess,
  type PaywallResourceRef,
  type UnifiedSubscription,
  type UnifiedTeam,
  type UnifiedPaywallState,
} from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";
import type { Trello } from "types/my-trello-types";
import { prodLog } from "@v2/shared/lib/logger";
import { PaywallResourceMapper } from "@v2/entities/paywall/resource/resource.map";
import { IframeInitializer } from "@v2/shared/api/trello";
import {
  createSessionPaywallStateStorage,
  type CachedPaywallState,
  type PaywallStateStorage,
} from "./paywallStateStorage";

// AIDEV-NOTE: Cache schema stamp; bump to force invalidation when payload shape evolves
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 3 * 60 * 60 * 1000;

const DEFAULT_STORAGE = createSessionPaywallStateStorage();
const inFlightPrefetches = new Map<string, Promise<void>>();

// AIDEV-NOTE: It seems running it in iframe connector would generates a huge number of function requests. Better leave this off there.
export async function prefetchAccessFromPaywall(
  trello: Trello.PowerUp.IFrame,
  options?: { storage?: PaywallStateStorage; force?: boolean }
): Promise<void> {
  if (typeof window === "undefined") {
    return;
  }

  const storage = options?.storage ?? DEFAULT_STORAGE;
  const force = options?.force ?? false;
  const paywallUrl = process.env.PAYWALL_URL;
  if (!paywallUrl) {
    prodLog("Paywall access prefetch skipped - PAYWALL_URL missing");
    return;
  }

  const appKey = process.env.API_KEY;
  if (!appKey) {
    prodLog("Paywall access prefetch skipped - API_KEY missing");
    return;
  }

  const context = trello.getContext();
  const appId = (context as { app?: string }).app ?? "bulk-actions";
  const memberId = context.member;
  const boardId = context.board;
  const cacheKey = buildCacheKey(memberId, boardId);

  const existing = readCachedPaywallState(memberId, boardId, storage);
  if (existing && !force) {
    prodLog("Paywall access prefetch skipped - cache hit", {
      expiresAt: existing.expiresAt,
      memberId,
      boardId,
    });
    return;
  }

  const inFlight = inFlightPrefetches.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const prefetchPromise = (async () => {
    try {
      const restApi = IframeInitializer.getRestApi(trello);
      const token = await restApi?.getToken?.();
      if (!token) {
        prodLog("Paywall access prefetch skipped - missing token", {
          memberId,
          boardId,
        });
        return;
      }

      const { session, bootstrap } = await prefetchAccessBootstrap({
        baseUrl: paywallUrl,
        credentials: { token, appKey },
        appId,
      });

      if (!session) {
        prodLog("Paywall access prefetch skipped - missing session", {
          memberId,
          boardId,
        });
        return;
      }

      const resources = PaywallResourceMapper.toPaywallResources(context);
      const subscriptions = (bootstrap?.subscriptions ??
        []) as UnifiedSubscription[];
      const ownedTeams = transformTeams(
        bootstrap?.teamsOwned as unknown as UnifiedTeam[] | undefined
      );
      const resourceAccess = calculateResourceAccess(subscriptions, resources);

      const state = buildUnifiedPaywallState({
        authStatus: session.status,
        subscriptions,
        resourceAccess,
        ownedTeams,
      });

      persistUnifiedPaywallState({
        state,
        memberId,
        boardId,
        appId,
        storage,
      });
    } catch (error) {
      prodLog("Paywall access prefetch failed", { error, memberId, boardId });
    }
  })();

  inFlightPrefetches.set(cacheKey, prefetchPromise);
  prefetchPromise.finally(() => {
    inFlightPrefetches.delete(cacheKey);
  });

  return prefetchPromise;
}

export function readCachedPaywallState(
  memberId?: string,
  boardId?: string,
  storage: PaywallStateStorage = DEFAULT_STORAGE
): CachedPaywallState | null {
  if (typeof window === "undefined") {
    return null;
  }

  const key = buildCacheKey(memberId, boardId);
  const cached = storage.read(key);
  if (!cached) return null;

  if (cached.expiresAt <= Date.now() || cached.version !== CACHE_VERSION) {
    storage.delete(key);
    return null;
  }

  return cached;
}

export function clearCachedPaywallState(
  memberId?: string,
  boardId?: string,
  storage: PaywallStateStorage = DEFAULT_STORAGE
) {
  const key = buildCacheKey(memberId, boardId);
  storage.delete(key);
}

export function persistUnifiedPaywallState(params: {
  state: UnifiedPaywallState;
  memberId?: string;
  boardId?: string;
  appId?: string;
  storage?: PaywallStateStorage;
  ttlMs?: number;
}) {
  if (typeof window === "undefined") {
    return;
  }

  const {
    state,
    memberId,
    boardId,
    appId,
    storage = DEFAULT_STORAGE,
    ttlMs = CACHE_TTL_MS,
  } = params;

  const snapshot: CachedPaywallState = {
    version: CACHE_VERSION,
    fetchedAt: Date.now(),
    expiresAt: Date.now() + ttlMs,
    state,
    source: { memberId, boardId, appId },
  };

  storage.write(buildCacheKey(memberId, boardId), snapshot);
}

function calculateResourceAccess(
  subscriptions: UnifiedSubscription[],
  resources: PaywallResourceRef[]
): PaywallResourceAccess[] {
  return resources.map((resource) => ({
    resource,
    hasAccess: filterGrantingSubscriptions(subscriptions, resource).length > 0,
  }));
}

function buildCacheKey(memberId?: string, boardId?: string) {
  return `${CACHE_VERSION}:${memberId ?? "unknown"}:${boardId ?? "unknown"}`;
}
